import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const emptyForm = {
  name: '',
  contact_name: '',
  address: '',
  email: '',
  phone: '',
  order_method: 'email',
  order_email: '',
  portal_url: '',
  notes: ''
};

const SettingsB2BPartnerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [lookupStatus, setLookupStatus] = useState('idle');
  const [lookupError, setLookupError] = useState('');
  const lookupTimeoutRef = useRef(null);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    const loadPartner = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const res = await fetch(`/api/b2b/partners/${id}`, { headers });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || 'Kunne ikke hente partner');
        setFormData({
          name: data.name || '',
          contact_name: data.contact_name || '',
          address: data.address || '',
          email: data.email || '',
          phone: data.phone || '',
          order_method: data.order_method || 'email',
          order_email: data.order_email || '',
          portal_url: data.portal_url || '',
          notes: data.notes || ''
        });
      } catch (err) {
        setError(err.message || 'Kunne ikke hente partner');
      } finally {
        setLoading(false);
      }
    };
    loadPartner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const applyLookupData = (lookupData) => {
    setFormData((prev) => ({
      ...prev,
      name: prev.name || lookupData.name || '',
      address: prev.address || lookupData.address || '',
      phone: prev.phone || lookupData.phone || '',
      portal_url: prev.portal_url || lookupData.website || ''
    }));
  };

  const fetchGoogleLookup = async (query) => {
    setLookupStatus('loading');
    setLookupError('');
    try {
      const res = await fetch(`/api/b2b/lookup?query=${encodeURIComponent(query)}`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Kunne ikke hente data');
      applyLookupData(data);
      setLookupStatus('success');
    } catch (err) {
      setLookupStatus('error');
      setLookupError(err.message || 'Kunne ikke hente data');
    }
  };

  useEffect(() => {
    if (id) return;
    const query = formData.name.trim();
    if (query.length < 3) {
      setLookupStatus('idle');
      setLookupError('');
      if (lookupTimeoutRef.current) clearTimeout(lookupTimeoutRef.current);
      return;
    }
    if (formData.phone || formData.address || formData.portal_url) return;
    if (lookupTimeoutRef.current) clearTimeout(lookupTimeoutRef.current);
    lookupTimeoutRef.current = setTimeout(() => {
      fetchGoogleLookup(query);
    }, 600);
    return () => {
      if (lookupTimeoutRef.current) clearTimeout(lookupTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.name, id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await fetch(id ? `/api/b2b/partners/${id}` : '/api/b2b/partners', {
        method: id ? 'PUT' : 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Kunne ikke gemme partner');
      navigate('/indstillinger/b2b');
    } catch (err) {
      setError(err.message || 'Kunne ikke gemme partner');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          {id ? 'Rediger partner' : 'Ny partner'}
        </h1>
      </div>

      {error && <div className="mb-3 p-3 bg-red-100 text-red-700 rounded">{error}</div>}

      {loading ? (
        <div className="text-gray-500">Indlæser...</div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-4 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Navn *</label>
                <button
                  type="button"
                  onClick={() => fetchGoogleLookup(formData.name.trim())}
                  className="text-xs text-falck-blue hover:underline disabled:opacity-50"
                  disabled={lookupStatus === 'loading' || formData.name.trim().length < 3}
                >
                  {lookupStatus === 'loading' ? 'Henter...' : 'Hent fra Google'}
                </button>
              </div>
              <input
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
              />
              {lookupError && <p className="mt-1 text-xs text-red-600">{lookupError}</p>}
              {!lookupError && lookupStatus === 'success' && (
                <p className="mt-1 text-xs text-green-600">Data hentet fra Google.</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Kontaktperson</label>
              <input
                value={formData.contact_name}
                onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Adresse</label>
              <input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
              />
              <p className="mt-1 text-xs text-gray-500">Email skal indtastes manuelt.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Telefon</label>
              <input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Bestillingsmetode</label>
              <select
                value={formData.order_method}
                onChange={(e) => setFormData({ ...formData, order_method: e.target.value })}
                className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
              >
                <option value="email">Email</option>
                <option value="portal">Portal</option>
                <option value="manual">Manual</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Bestillingsmail</label>
              <input
                value={formData.order_email}
                onChange={(e) => setFormData({ ...formData, order_email: e.target.value })}
                className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Portal URL</label>
            <input
              value={formData.portal_url}
              onChange={(e) => setFormData({ ...formData, portal_url: e.target.value })}
              className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Noter</label>
            <textarea
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => navigate('/indstillinger/b2b')}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md"
            >
              Annuller
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-falck-blue text-white rounded-md disabled:opacity-60"
            >
              {saving ? 'Gemmer...' : 'Gem'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default SettingsB2BPartnerForm;
